import {
  PageSpecifications,
  PageAspectRatio,
  PageFormat,
  PageLayerPanel,
  BuilderPanel,
  BuilderEvents,
  PageGridConstants,
  OverflowStyles,
} from '@/modules/ta/reportstudio/reportstudio.constants';
import { hexToRGBA } from '@/modules/core/app/helpers/ColorHelper';
import EventBus from '@/modules/core/app/helpers/EventBus';
import { getters } from '@/modules/core/app/helpers/store';

export class ReportStudioPageStylesUIFactory {
  /**
   *
   * @type {number}
   * @private
   */
  static minimumZoomPercent = 15;

  /**
   *
   * @type {{left: number, right: number}}
   */
  static originalPageMargins = {
    left: 5,
    right: 5,
  };

  /**
   *
   * @param aspectRatio
   * @returns {number[]}
   */
  static getPageWidthAndHeight(aspectRatio) {
    let height = PageSpecifications.WIDTH / aspectRatio;
    // export requires exact int values as px specified in puppeteer microservice
    if (getters.export?.isExporting()) {
      height = Math.trunc(height);
    }

    return [PageSpecifications.WIDTH, height];
  }

  /**
   *
   * @param aspectRatio
   * @param isLandscape
   * @returns {{}}
   */
  static pageStyle(aspectRatio, isLandscape) {
    const [pageWidth, pageHeight] = this.getPageWidthAndHeight(aspectRatio);

    const styles = {};

    styles.height = `${pageWidth}px`;
    styles.width = `${pageHeight}px`;

    if (isLandscape) {
      [styles.height, styles.width] = [styles.width, styles.height];
    }

    return styles;
  }

  /**
   *
   * @param aspectRatio
   * @param isLandscape
   * @param page
   * @returns {{width: string, height: string}}
   */
  static pageBackgroundStyle(aspectRatio, isLandscape, page) {
    const [pageWidth, pageHeight] = this.getPageWidthAndHeight(aspectRatio);

    const styles = {
      height: `${pageHeight}px`,
      width: `${pageWidth}px`,
    };

    if (page.metadata.design_options && page.metadata.design_options.background_color) {
      const color = hexToRGBA(
        page.metadata.design_options.background_color,
        page.metadata.design_options.opacity
      );
      // eslint-disable-next-line tap/no-raw-text-js
      styles['background-color'] = `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
    }

    if (!isLandscape) {
      // eslint-disable-next-line tap/no-raw-text-js
      styles.transform = 'rotate(90deg)';
    }

    return styles;
  }

  /**
   *
   * @param aspectRatio
   * @param isLandscape
   * @param isPresentationMode
   * @returns {{width: string, height: string}}
   */
  static pageBackgroundOverlayStyle(aspectRatio, isLandscape, isPresentationMode) {
    const [pageWidth, pageHeight] = this.getPageWidthAndHeight(aspectRatio);

    const styles = {
      height: `${pageHeight}px`,
      width: `${pageWidth}px`,
    };

    if (!isLandscape) {
      // eslint-disable-next-line tap/no-raw-text-js
      styles.transform = 'rotate(90deg)';
    }

    if (isPresentationMode) {
      // eslint-disable-next-line tap/no-raw-text-js
      styles.display = 'none';
    }

    return styles;
  }

  /**
   *
   * @param aspectRatio
   * @param isLandscape
   * @param overFlowHidden
   * @returns {{}}
   */
  static pageContentStyle(aspectRatio, isLandscape, overFlowHidden) {
    const [pageWidth, pageHeight] = this.getPageWidthAndHeight(aspectRatio);

    const styles = {};
    styles.height = `${pageWidth}px`;
    styles.width = `${pageHeight}px`;

    if (isLandscape) {
      [styles.height, styles.width] = [styles.width, styles.height];
    }

    if (overFlowHidden) {
      styles.overflow = OverflowStyles.HIDDEN;
    }

    return styles;
  }

  static gridSize(aspectRatio, isLandscape) {
    const [pageWidth, pageHeight] = this.getPageWidthAndHeight(aspectRatio);

    const styles = {};
    styles.height = pageWidth;
    styles.width = pageHeight;

    if (isLandscape) {
      [styles.height, styles.width] = [styles.width, styles.height];
    }

    styles.width /= PageGridConstants.Y;
    styles.height /= PageGridConstants.Y;

    return styles;
  }

  static calculateZoom(report, isPresentationMode, fitToPage) {
    let yZoom;
    let xZoom;
    let canvasHeight;
    let canvasWidth;

    // TODO: @Ramakrishna
    // never change the zoom when exporting
    // if (sharedData.readOnly) {
    //   return;
    // }

    const result = {};
    result.fitToPage = fitToPage;

    const aspectRatio =
      report?.format !== PageFormat.STANDARD
        ? PageAspectRatio.WIDESCREEN
        : PageAspectRatio.STANDARD;

    let pageWidth = PageSpecifications.WIDTH / aspectRatio; // original page width
    let pageHeight = PageSpecifications.WIDTH;

    if (report.isOrientationLandscape()) {
      [pageWidth, pageHeight] = [pageHeight, pageWidth];
    }

    if (isPresentationMode) {
      canvasWidth = window.innerWidth;
      canvasHeight = window.innerHeight;
      xZoom = (canvasWidth / pageWidth) * 100;
      yZoom = (canvasHeight / pageHeight) * 100;

      result.marginTop = 0;
      result.zoom = Math.min(yZoom, xZoom);

      EventBus.signal(BuilderEvents.ZOOM_CALCULATED, result);
      return;
    }

    canvasWidth = window.innerWidth - PageLayerPanel.WIDTH - BuilderPanel.WIDTH;
    canvasHeight = window.innerHeight - 100;

    xZoom =
      ((canvasWidth - this.originalPageMargins.left - this.originalPageMargins.right) / pageWidth) *
      100;
    yZoom =
      ((canvasHeight - this.originalPageMargins.left - this.originalPageMargins.right) /
        pageHeight) *
      100;

    result.zoom = Math.min(yZoom, xZoom);

    if (fitToPage) {
      result.zoom = xZoom;

      result.marginTop = ((PageSpecifications.WIDTH * result.zoom) / 100 - canvasHeight) / 2 + 40;
      if (result.marginTop < 0) {
        result.marginTop = 0;
      }
    } else {
      result.marginTop = 0;
    }

    if (result.zoom < this.minimumZoomPercent) {
      result.zoom = this.minimumZoomPercent;
    }

    EventBus.signal(BuilderEvents.ZOOM_CALCULATED, result);
  }
}
